import { compareTemporaryOncologyDoctors } from 'utils/temporaryOncologyDoctorOrder';
import { CareProvider, Department, FORM_SECTION, ServiceItem } from 'entities/Form';
import {
  AvailableTimeslotV2,
  BookingModeV2,
  CareProviderV2,
  DepartmentV2,
  FormValuesV2,
  ServiceItemV2,
  SpecialtyV2,
} from 'entities/FormV2';
import { getLocalizedValueByLanguageV2 } from 'utils/helperV2';

export const isV2BookingMode = (mode?: BookingModeV2) =>
  mode === BookingModeV2.Doctor || mode === BookingModeV2.Specialty;

export const getEffectiveSiteId = (mode: BookingModeV2 | undefined, selectedSiteId = '') => {
  if (mode === BookingModeV2.EKMC) return 'EKMC';
  if (mode === BookingModeV2.BH) return 'BH';
  return selectedSiteId;
};

export const getFixedDepartmentValue = (mode?: BookingModeV2) => {
  if (mode === BookingModeV2.EKMC) return 'EKOPC_OPCCONS';
  if (mode === BookingModeV2.BH) return 'OPC_OPC02';
  return undefined;
};

export const getBookingOnLoadConfig = (mode: BookingModeV2 | undefined) => {
  if (!mode) return undefined;
  return {
    version: isV2BookingMode(mode) ? ('v2' as const) : ('v1' as const),
    cmsSiteId: getEffectiveSiteId(mode),
  };
};

export const getInitialFormSection = (mode?: BookingModeV2) => {
  if (mode === BookingModeV2.Doctor) return FORM_SECTION.DOCTOR;
  if (mode === BookingModeV2.Specialty) return FORM_SECTION.SPECIALTY;
  return FORM_SECTION.DEPARTMENT;
};

export interface DoctorSpecialtyOptionV2 {
  key: string;
  careProvider: CareProviderV2;
  specialty: SpecialtyV2;
  departments: DepartmentV2[];
}

export interface AggregatedSpecialtyOptionV2 {
  key: string;
  specialty: SpecialtyV2;
  specialties: SpecialtyV2[];
  departments: DepartmentV2[];
}

export interface SpecialtyDoctorOptionV2 {
  careProvider: CareProviderV2;
  departments: DepartmentV2[];
}

export interface MasterServiceItemSourceV2 {
  careProvider: CareProviderV2;
  department: DepartmentV2;
  serviceItem: ServiceItemV2;
}

export interface MasterServiceItemOptionV2 {
  specialty?: SpecialtyV2;
  masterServiceItem: ServiceItemV2['masterServiceItem'];
  sources: MasterServiceItemSourceV2[];
  minDate: string;
  maxDate: string;
}

const optionCollatorV2 = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const DOCTOR_MODE_HIDDEN_CARE_PROVIDER_CODE = '0000';
const SPECIALTY_MODE_LAST_CARE_PROVIDER_VALUE = '0000';

const compareCareProvidersV2 = (left: CareProviderV2, right: CareProviderV2) =>
  optionCollatorV2.compare(left.nameEn, right.nameEn) || optionCollatorV2.compare(left.value, right.value);

const compareSpecialtyCareProvidersV2 = (left: CareProviderV2, right: CareProviderV2) => {
  const leftIsLast = left.value === SPECIALTY_MODE_LAST_CARE_PROVIDER_VALUE;
  const rightIsLast = right.value === SPECIALTY_MODE_LAST_CARE_PROVIDER_VALUE;

  if (leftIsLast !== rightIsLast) return leftIsLast ? 1 : -1;

  return compareCareProvidersV2(left, right);
};

const compareMasterServiceItemOptionsV2 = (left: MasterServiceItemOptionV2, right: MasterServiceItemOptionV2) =>
  left.masterServiceItem.ordering - right.masterServiceItem.ordering ||
  optionCollatorV2.compare(left.masterServiceItem.nameEn, right.masterServiceItem.nameEn) ||
  optionCollatorV2.compare(left.masterServiceItem.nameTc, right.masterServiceItem.nameTc) ||
  optionCollatorV2.compare(left.masterServiceItem.nameSc, right.masterServiceItem.nameSc) ||
  optionCollatorV2.compare(left.masterServiceItem.hid, right.masterServiceItem.hid);

export const normalizeSearchValueV2 = (value: string) =>
  value.normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, '');

export const getSpecialtyIdentityV2 = (specialty: SpecialtyV2) =>
  JSON.stringify([specialty.nameEn, specialty.nameTc, specialty.nameSc].map(normalizeSearchValueV2));

export const getDoctorSpecialtyKey = (careProviderValue: string, specialty: SpecialtyV2) =>
  JSON.stringify([careProviderValue, getSpecialtyIdentityV2(specialty)]);

const filterSpecialtyOptionsV2 = (
  specialtyOptions?: SpecialtyV2[],
  isCareProviderAllowed: (careProvider: CareProviderV2) => boolean = () => true,
): SpecialtyV2[] | undefined =>
  specialtyOptions
    ?.map((specialty) => {
      const departmentOptions = specialty.departmentOptions
        ?.map((department) => {
          const careProviders = department.careProviders
            ?.map((careProvider) => {
              if (!isCareProviderAllowed(careProvider)) return undefined;

              const serviceItems = careProvider.serviceItems?.filter(
                (serviceItem) => !!serviceItem.masterServiceItem?.hid,
              );

              return serviceItems?.length ? { ...careProvider, serviceItems } : undefined;
            })
            .filter(isDefined);

          return careProviders?.length ? { ...department, careProviders } : undefined;
        })
        .filter(isDefined);

      return departmentOptions?.length ? { ...specialty, departmentOptions } : undefined;
    })
    .filter(isDefined);

export const filterBookableSpecialtyOptionsV2 = (specialtyOptions?: SpecialtyV2[]): SpecialtyV2[] | undefined =>
  filterSpecialtyOptionsV2(specialtyOptions);

export const postProcessBookingOnLoadSpecialtyOptionsV2 = (
  specialtyOptions: SpecialtyV2[] | undefined,
  bookingMode: BookingModeV2,
): SpecialtyV2[] | undefined =>
  filterSpecialtyOptionsV2(
    bookingMode === BookingModeV2.Specialty
      ? specialtyOptions?.filter((specialty) => specialty.isShow)
      : specialtyOptions,
    (careProvider) =>
      bookingMode !== BookingModeV2.Doctor || careProvider.code !== DOCTOR_MODE_HIDDEN_CARE_PROVIDER_CODE,
  );

export const matchesLocalizedNameV2 = (filterValue: string, names: Array<string | undefined>) => {
  const normalizedFilterValue = normalizeSearchValueV2(filterValue);
  if (!normalizedFilterValue) return true;

  return names.some((name) => !!name && normalizeSearchValueV2(name).includes(normalizedFilterValue));
};

export const filterDoctorSpecialtyOptionsV2 = (
  doctorOptions: DoctorSpecialtyOptionV2[] | undefined,
  keyword: string,
) => {
  if (!normalizeSearchValueV2(keyword)) return [];

  return doctorOptions?.filter((option) =>
    matchesLocalizedNameV2(keyword, [
      option.careProvider.name,
      option.careProvider.nameEn,
      option.careProvider.nameTc,
      option.careProvider.nameSc,
      option.specialty.value,
      option.specialty.nameEn,
      option.specialty.nameTc,
      option.specialty.nameSc,
    ]),
  );
};

export const getDoctorSpecialtyOptions = (specialtyOptions?: SpecialtyV2[]) => {
  if (!specialtyOptions) return undefined;

  const optionMap = new Map<string, DoctorSpecialtyOptionV2>();

  specialtyOptions.forEach((specialty) => {
    specialty.departmentOptions?.forEach((department) => {
      department.careProviders?.forEach((careProvider) => {
        const key = getDoctorSpecialtyKey(careProvider.value, specialty);
        const currentOption = optionMap.get(key);

        if (!currentOption) {
          optionMap.set(key, {
            key,
            careProvider,
            specialty,
            departments: [department],
          });
          return;
        }

        if (!currentOption.departments.some((option) => option.value === department.value)) {
          currentOption.departments.push(department);
        }
      });
    });
  });

  return Array.from(optionMap.values()).sort(
    (left, right) =>
      compareCareProvidersV2(left.careProvider, right.careProvider) ||
      optionCollatorV2.compare(left.specialty.nameEn, right.specialty.nameEn),
  );
};

export const getAggregatedSpecialtyOptionsV2 = (specialtyOptions?: SpecialtyV2[]) => {
  if (!specialtyOptions) return undefined;

  const optionMap = new Map<string, AggregatedSpecialtyOptionV2>();

  specialtyOptions.forEach((specialty) => {
    const key = getSpecialtyIdentityV2(specialty);
    const currentOption = optionMap.get(key);

    if (!currentOption) {
      optionMap.set(key, {
        key,
        specialty,
        specialties: [specialty],
        departments: [...(specialty.departmentOptions || [])],
      });
      return;
    }

    if (!currentOption.specialties.some((option) => option.value === specialty.value)) {
      currentOption.specialties.push(specialty);
    }
    specialty.departmentOptions?.forEach((department) => {
      currentOption.departments.push(department);
    });
  });

  return Array.from(optionMap.values());
};

export const filterAggregatedSpecialtyOptionsV2 = (
  specialtyOptions: AggregatedSpecialtyOptionV2[] | undefined,
  keyword: string,
) =>
  specialtyOptions?.filter((option) =>
    matchesLocalizedNameV2(
      keyword,
      option.specialties.flatMap((specialty) => [
        specialty.code,
        specialty.value,
        specialty.nameEn,
        specialty.nameTc,
        specialty.nameSc,
      ]),
    ),
  );

export const getSpecialtyDoctorOptionsV2 = (specialtyOption?: AggregatedSpecialtyOptionV2) => {
  if (!specialtyOption) return [];

  const optionMap = new Map<string, SpecialtyDoctorOptionV2>();

  specialtyOption.departments.forEach((department) => {
    department.careProviders?.forEach((careProvider) => {
      const currentOption = optionMap.get(careProvider.value);
      if (!currentOption) {
        optionMap.set(careProvider.value, { careProvider, departments: [department] });
        return;
      }

      if (!currentOption.departments.some((option) => option.value === department.value)) {
        currentOption.departments.push(department);
      }
    });
  });

  return Array.from(optionMap.values()).sort((left, right) =>
    compareTemporaryOncologyDoctors(specialtyOption.specialty, left.careProvider, right.careProvider) ||
    compareSpecialtyCareProvidersV2(left.careProvider, right.careProvider),
  );
};

const addMasterServiceItemSource = (
  optionMap: Map<string, MasterServiceItemOptionV2>,
  careProvider: CareProviderV2,
  department: DepartmentV2,
  serviceItem: ServiceItemV2,
) => {
  const masterServiceItemHid = serviceItem.masterServiceItem.hid;
  const currentOption = optionMap.get(masterServiceItemHid);
  const sourceKey = JSON.stringify([careProvider.value, department.value, serviceItem.value]);

  if (!currentOption) {
    optionMap.set(masterServiceItemHid, {
      masterServiceItem: serviceItem.masterServiceItem,
      sources: [{ careProvider, department, serviceItem }],
      minDate: serviceItem.minDate,
      maxDate: serviceItem.maxDate,
    });
    return;
  }

  const hasSource = currentOption.sources.some(
    (source) =>
      JSON.stringify([source.careProvider.value, source.department.value, source.serviceItem.value]) === sourceKey,
  );
  if (!hasSource) currentOption.sources.push({ careProvider, department, serviceItem });

  if (new Date(serviceItem.minDate) < new Date(currentOption.minDate)) {
    currentOption.minDate = serviceItem.minDate;
  }
  if (new Date(serviceItem.maxDate) > new Date(currentOption.maxDate)) {
    currentOption.maxDate = serviceItem.maxDate;
  }
};

export const getMasterServiceItemOptions = (doctorOption?: DoctorSpecialtyOptionV2) => {
  if (!doctorOption) return [];

  const optionMap = new Map<string, MasterServiceItemOptionV2>();

  doctorOption.departments.forEach((department) => {
    department.careProviders
      ?.filter((careProvider) => careProvider.value === doctorOption.careProvider.value)
      .forEach((careProvider) => {
        careProvider.serviceItems?.forEach((serviceItem) => {
          addMasterServiceItemSource(optionMap, careProvider, department, serviceItem);
        });
      });
  });

  return Array.from(optionMap.values()).sort(compareMasterServiceItemOptionsV2);
};

export const getSpecialtyMasterServiceItemOptionsV2 = (
  specialtyOption?: AggregatedSpecialtyOptionV2,
  preferredCareProviderHid?: string,
) => {
  if (!specialtyOption) return [];

  const optionMap = new Map<string, MasterServiceItemOptionV2>();

  specialtyOption.departments.forEach((department) => {
    department.careProviders
      ?.filter((careProvider) => !preferredCareProviderHid || careProvider.value === preferredCareProviderHid)
      .forEach((careProvider) => {
        careProvider.serviceItems?.forEach((serviceItem) => {
          addMasterServiceItemSource(optionMap, careProvider, department, serviceItem);
        });
      });
  });

  return Array.from(optionMap.values())
    .sort(compareMasterServiceItemOptionsV2)
    .map((option) => ({ ...option, specialty: specialtyOption.specialty }));
};

export const getEligibleCareProvidersV2 = (masterServiceItem?: MasterServiceItemOptionV2) => {
  if (!masterServiceItem) return [];

  const careProviderMap = new Map<string, CareProviderV2>();
  masterServiceItem.sources.forEach(({ careProvider }) => {
    if (!careProviderMap.has(careProvider.value)) {
      careProviderMap.set(careProvider.value, careProvider);
    }
  });

  return Array.from(careProviderMap.values()).sort(
    (left, right) =>
      compareTemporaryOncologyDoctors(masterServiceItem.specialty, left, right) ||
      compareSpecialtyCareProvidersV2(left, right)
  );
};

export const getDepartmentRemindersV2 = (
  timeSlotGroups: { careProviderHid?: string; availableTimeslots: AvailableTimeslotV2[] }[],
  sources: MasterServiceItemSourceV2[],
  language: string,
): string[] => {
  const noteTexts = new Set<string>();
  const siteReminders = new Set<string>();

  timeSlotGroups.forEach(({ careProviderHid, availableTimeslots }) => {
    availableTimeslots.forEach(({ department, serviceItemHid }) => {
      const source = sources.find(
        (source) =>
          (!careProviderHid || source.careProvider.value === careProviderHid) &&
          source.department.hid === department.hid &&
          source.serviceItem.value === serviceItemHid,
      );
      const rawNotes = source?.department.notes;
      // Ignore blank notes, but compare all other text exactly as supplied.
      const notes = rawNotes?.trim() ? rawNotes : '';
      noteTexts.add(notes);
      if (!notes) return;

      const siteName =
        getLocalizedValueByLanguageV2(
          {
            hid: department.hid,
            nameEn: department.siteNameEn,
            nameTc: department.siteNameTc,
            nameSc: department.siteNameSc,
          },
          language,
        ) || department.siteNameEn || department.siteCode;
      siteReminders.add(siteName ? `${siteName} : ${notes}` : notes);
    });
  });

  return noteTexts.size === 1 ? Array.from(noteTexts).filter(Boolean) : Array.from(siteReminders);
};

export const mapDoctorTimeslotToFormValuesV2 = (
  formValues: FormValuesV2,
  timeSlot: AvailableTimeslotV2,
  sources: MasterServiceItemSourceV2[] = [],
  careProviderHid?: string,
): FormValuesV2 => ({
  ...formValues,
  departmentValue:
    sources.find(
      (source) =>
        (!careProviderHid || source.careProvider.value === careProviderHid) &&
        source.department.hid === timeSlot.department.hid &&
        source.serviceItem.value === timeSlot.serviceItemHid,
    )?.department.value || timeSlot.department.hid,
  departmentHid: timeSlot.department.hid,
  siteCode: timeSlot.department.siteCode,
  careProviderHid: careProviderHid || formValues.careProviderHid,
  serviceItemHid: timeSlot.serviceItemHid,
  fromDt: timeSlot.fromDt,
  toDt: timeSlot.toDt,
});

const toLegacyServiceItem = (serviceItem: ServiceItemV2): ServiceItem => ({
  value: serviceItem.value,
  name: serviceItem.name,
  nameTc: serviceItem.nameTc,
  nameSc: serviceItem.nameSc,
  minDate: serviceItem.minDate,
  maxDate: serviceItem.maxDate,
  buildingFloor: serviceItem.buildingFloor
    ? {
        value: serviceItem.buildingFloor.value,
        name: serviceItem.buildingFloor.name,
        nameTc: serviceItem.buildingFloor.nameTc,
        nameSc: serviceItem.buildingFloor.nameSc,
      }
    : undefined,
});

const toLegacyCareProvider = (careProvider: CareProviderV2): CareProvider => ({
  value: careProvider.value,
  name: careProvider.name,
  code: careProvider.code,
  imgPath: careProvider.imgPath,
  position: careProvider.position,
  title: careProvider.title,
  qualifications: careProvider.qualifications,
  serviceItems: careProvider.serviceItems?.map(toLegacyServiceItem),
});

const mergeCareProviders = (careProviders: CareProvider[], nextCareProviders: CareProvider[]) => {
  const careProviderMap = new Map(careProviders.map((careProvider) => [careProvider.value, careProvider]));

  nextCareProviders.forEach((careProvider) => {
    const currentCareProvider = careProviderMap.get(careProvider.value);
    if (!currentCareProvider) {
      careProviderMap.set(careProvider.value, careProvider);
      return;
    }

    const serviceItemMap = new Map(
      (currentCareProvider.serviceItems || []).map((serviceItem) => [serviceItem.value, serviceItem]),
    );
    careProvider.serviceItems?.forEach((serviceItem) => serviceItemMap.set(serviceItem.value, serviceItem));
    currentCareProvider.serviceItems = Array.from(serviceItemMap.values());
  });

  return Array.from(careProviderMap.values());
};

export const getLegacyDepartmentOptions = (specialtyOptions?: SpecialtyV2[]) => {
  if (!specialtyOptions) return undefined;

  const departmentMap = new Map<string, Department>();

  specialtyOptions.forEach((specialty) => {
    specialty.departmentOptions?.forEach((department) => {
      const careProviders = (department.careProviders || []).map(toLegacyCareProvider);
      const currentDepartment = departmentMap.get(department.value);

      if (!currentDepartment) {
        departmentMap.set(department.value, {
          hid: department.hid,
          value: department.value,
          name: department.name,
          flow: department.flow,
          notes: department.notes,
          careProviders,
        });
        return;
      }

      currentDepartment.careProviders = mergeCareProviders(currentDepartment.careProviders || [], careProviders);
    });
  });

  return Array.from(departmentMap.values());
};
